import { graphql, Link } from 'gatsby';
import React, { useEffect, useMemo, useState } from 'react';
import PageMetas from '../components/PageMetas';
import { ReactElement } from 'react';
import {
  CustomPageContext,
  IPost,
  IPostCategory,
  Language,
  StrapiSlugLocalizations,
  AppContextInterface,
} from '../custom-types';
import { AdvancedImage, responsive, placeholder, lazyload } from '@cloudinary/react';
import { AppCtx } from '../layouts';
import { TECollapse } from 'tw-elements-react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import I18nPageTranslations from '../graphql/fragments/i18n';
import useLocalizationSwitcher from '../hooks/useLocalizationSwitcher';
import useCloudinaryImage from '../hooks/useCloudinaryImage';
import moment from 'moment';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import PostRelatedArticles from '../components/post/PostRelatedArticles';
import Breadcrumbs from '../components/Breadcrumbs';
import PostAuthor from '../components/post/PostAuthor';

export function Head({
  data,
  pageContext,
}: {
  data: Queries.BlogPostPageQuery;
  pageContext: CustomPageContext;
}): ReactElement {
  const { i18n } = useI18next();
  const postData = data?.strapiBlogPost;

  const postCategory: IPostCategory = useMemo(() => {
    const post: IPost = postData as IPost;
    return post?.categories?.reduce(
      (prev: IPostCategory, current: IPostCategory) => (prev?.weight < current?.weight ? prev : current),
      0
    );
  }, [postData?.categories]);

  return (
    <PageMetas
      title={data?.strapiBlogPost?.title as string}
      author={(data?.strapiBlogPost?.expert?.name ?? data?.strapiBlogPost?.author?.name) as string}
      authorTitle={(data?.strapiBlogPost?.expert?.title ?? data?.strapiBlogPost?.author?.title) as string}
      authorSlug={(data?.strapiBlogPost?.expert?.slug ?? data?.strapiBlogPost?.author?.slug) as string}
      publicationDate={data?.strapiBlogPost?.publish_at ?? data?.strapiBlogPost?.publishedAt}
      modifiedDate={data?.strapiBlogPost?.updatedAt}
      image={data?.strapiBlogPost?.image?.url}
      metaDescription={data?.strapiBlogPost?.seo?.metaDescription as string}
      keywords={data?.strapiBlogPost?.seo?.keywords as string}
      pageContext={pageContext}
      structuredData
      youtubeVideoId={data?.strapiBlogPost?.youtubeVideoId as string}
      categoryName={postCategory?.name}
      categorySlug={postCategory?.slug}
      lang={i18n.language}
      postSlug={data?.strapiBlogPost?.slug as string}
      hasAlternate={data?.strapiBlogPost?.localizations?.data?.length > 0}
    />
  );
}

const BlogPostPage = (props: { data: Queries.BlogPostPageQuery; pageContext: CustomPageContext }): ReactElement => {
  const postData = props?.data?.strapiBlogPost;
  const strapiPageLocalizationData = postData?.localizations?.data;
  const postImage = useCloudinaryImage(postData?.image?.url);
  const { appContext } = React.useContext<AppContextInterface>(AppCtx);
  const { i18n } = useI18next();
  const [tableOfContent, setTableOfContent] = useState([]);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    let agent = parseUserAgent(navigator.userAgent);
    setIsSafari(agent);
  }, []);

  const parseUserAgent = (pathname) => {
    const isSafari = /^((?!chrome).)*safari/i.test(pathname);
    return isSafari;
  };

  useLocalizationSwitcher({
    pathname: '/p',
    defaultLanguage: props.pageContext.defaultLanguage as Language,
    strapiSlugLocalizations: strapiPageLocalizationData as StrapiSlugLocalizations,
  });
  const { t } = useTranslation();

  const postCategory: IPostCategory = useMemo(() => {
    const post: IPost = postData as IPost;
    return post?.categories?.reduce(
      (prev: IPostCategory, current: IPostCategory) => (prev?.weight < current?.weight ? prev : current),
      0
    );
  }, [postData?.categories]);

  if (typeof document !== 'undefined') {
    Array.from(document.querySelectorAll('.markdown [style]')).forEach((elem: any) => {
      elem.style.removeProperty('font-family');
      elem.style.removeProperty('font-size');
      elem.style.removeProperty('color');
    });
  }

  //Table of content
  function tableOfContents() {
    const headings = document.querySelectorAll('h2');
    const headingData: any = [];
    if (headings.length < 2) return;
    headings.forEach((heading, index) => {
      const id = `h2-${index}`;
      const content = heading.textContent;
      heading.setAttribute('id', id);
      headingData.push({ id, content });
    });
    setTableOfContent(headingData);
  }

  //Set functionLazy Loading
  function setLazyLoading() {
    const images = document.querySelectorAll('img');

    images.forEach((img: any, index: any) => {
      img.setAttribute('loading', 'lazy');
    });
  }

  useEffect(() => {
    setLazyLoading();
    tableOfContents();
  }, []);

  //Author bio section
  const [activeElement, setActiveElement] = useState('');
  const handleClickAuthorSource = (value: string) => {
    if (value === activeElement) {
      setActiveElement('');
    } else {
      setActiveElement(value);
    }
  };
  const isDateInCurrentOrPreviousMonth = (date: any) => {
    const currentDate = moment();
    const targetDate = moment(date);

    return (
      (currentDate.isSame(targetDate, 'month') && currentDate.isSame(targetDate, 'year')) ||
      currentDate.subtract(1, 'month').isSame(targetDate, 'month') // Check for the previous month
    );
  };
  // Check if publish date is in current month or previous month
  const shouldShowPublishDate = isDateInCurrentOrPreviousMonth(postData?.publish_at ?? postData?.publishedAt);

  const addNofollowToLinks = (htmlString: any) => {
    return htmlString.replace(/<a\b[^>]*>/g, (match: any) => {
      if (!match.includes('rel="nofollow"')) {
        return match.replace('<a ', '<a rel="nofollow" ');
      }
      return match;
    });
  };

  return (
    <div className="">
      <div
        className={
          isSafari
            ? appContext?.currentLanguage === 'en'
              ? 'lg:max-w-5xl mx-auto my-0 p-6 mb-8 ltr:font-body flex flex-col text-omooma-dark rtl:font-bodyEn'
              : 'lg:max-w-5xl mx-auto my-0 p-6 mb-8 ltr:font-body flex flex-col text-omooma-dark rtl:font-bodyEn'
            : appContext?.currentLanguage !== 'en'
            ? 'lg:max-w-5xl mx-auto my-0 p-6 mb-8 ltr:font-body font-bodyEn flex flex-col text-omooma-dark rtl:font-bodyEn'
            : 'lg:max-w-5xl mx-auto my-0 p-6 mb-8 ltr:font-body font-body flex flex-col text-omooma-dark rtl:font-bodyEn'
        }
      >
        <Breadcrumbs
          categorySlug={postCategory?.slug}
          categoryTitle={postCategory?.name}
          defaultLanguage={props.pageContext.defaultLanguage}
        />
        <h1
          className={
            isSafari
              ? appContext?.currentLanguage === 'en'
                ? 'lg:my-8 my-3 mt-4 lg:text-4xl text-2xl ltr:font-headline font-headline text-omooma-pink-dark-primary'
                : 'lg:my-8 my-3 mt-4 lg:text-4xl text-2xl text-omooma-pink-dark-primary rtl:font-bodyEn'
              : appContext?.currentLanguage !== 'en'
              ? 'lg:my-8 font-bodyEn my-3 mt-4 lg:text-4xl text-2xl ltr:font-headline text-omooma-pink-dark-primary rtl:font-bodyEn'
              : 'lg:my-8  my-3 mt-4 lg:text-4xl text-2xl ltr:font-headline font-headline text-omooma-pink-dark-primary rtl:font-bodyEn'
          }
        >
          {postData?.title}
        </h1>
        <p className={`lg:mb-6 mb-4 text-omooma-dark-grey font-light  ltr:font-headline ${isSafari ? 'rtl:font-bodyEn ltr:font-headline' : 'rtl:font-bodyAr'} whitespace-pre-wrap`}>{postData?.excerpt}</p>
        <Link
          to={authorUrl(i18n.language, (postData?.expert?.slug ?? postData?.author?.slug) as string)}
          className="flex flex-row items-center"
        >
          <PostAuthor
            avatarUrl={(postData?.expert?.photo?.url ?? postData?.author?.photo?.url) as string}
            avatarAlt={(postData?.expert?.photo?.alternativeText ?? postData?.author?.photo?.alternativeText) as string}
            name={(postData?.expert?.name ?? postData?.author?.name) as string}
          />
        </Link>
        <div className="flex flex-row post-updated-date ltr:ml-9 rtl:mr-9 font-light">
          <span className={`ltr:ml-5 rtl:mr-5 ltr:font-headline ${isSafari ? 'rtl:font-bodyEn' : 'rtl:font-bodyAr'}`}>
            {appContext?.currentLanguage === 'en' ? 'Published on:' : 'تاريخ النشر:'}
          </span>
          <span className={`ltr:ml-1 rtl:mr-1 ltr:font-headline  ${isSafari ? 'rtl:font-bodyEn ltr:font-headline' : 'rtl:font-bodyAr'}`}>
            {moment(postData?.publish_at ?? postData?.publishedAt).format('MMM Do YYYY')}
          </span>
          {!shouldShowPublishDate && (
            <>
              <span className="rtl:mr-1 ltr:ml-1"> |</span>
              <span className={`ltr:ml-1 rtl:mr-1  ltr:font-headline ${isSafari ? 'rtl:font-bodyEn ltr:font-headline' : 'rtl:font-bodyAr'}`}>
                {appContext?.currentLanguage === 'en' ? 'Updated on:' : ' تاريخ التعديل :'}
              </span>
              <span className="text-gray-600 ltr:ml-1 rtl:mr-1">
                {moment().subtract(1, 'months').date(10).format('MMM Do YYYY')}
              </span>
            </>
          )}
        </div>
        {postData?.youtubeVideoId || postData?.dailymotionVideoId ? (
          <div className="my-4">
            {postData?.youtubeVideoId && (
              <div className="w-full my-6">
                <iframe
                  loading="lazy"
                  className="yt-video mb-10 w-full"
                  src={`https://www.youtube.com/embed/${postData?.youtubeVideoId}`}
                ></iframe>
              </div>
            )}
            {postData?.dailymotionVideoId && (
              <iframe
                loading="lazy"
                src={`https://www.dailymotion.com/embed/video/${postData?.dailymotionVideoId}?pubtool=oembed`}
                className="yt-video mb-10 w-full"
                scrolling="no"
                allow="encrypted-media;"
              ></iframe>
            )}
          </div>
        ) : (
          <div className="my-4">
            {postData?.video?.url && typeof window !== 'undefined' ? (
              <></>
            ) : (
              <div className="aspect-[16/9] bg-slate-200 rounded-xl overflow-hidden">
                {postImage && (
                  <AdvancedImage
                    cldImg={postImage}
                    alt={postData?.image?.alternativeText}
                    loading="lazy"
                    width="1000"
                    height="563"
                    plugins={[lazyload({ threshold: 0.25 }), responsive({ steps: 200 }), placeholder()]}
                  />
                )}
              </div>
            )}
          </div>
        )}
        {tableOfContent?.length > 0 && (
          <div className="w-full rounded-lg bg-omooma-light-blue flex flex-col p-6 lg:text-lg text-xs lg:font-normal font-light mb-5 table-content-line-height">
            <h3 className="text-xl mb-4 rtl:font-bodyEn whitespace-pre-wrap">{i18n.language === 'en' ? 'Table of content:' : 'جدول المحتويات'}</h3>
            {tableOfContent.map((item: any, index) => (
              <a key={item.id} href={`#${item.id}`} className="flex-row font-bold">
                <span className="text-omooma-pink-dark-primary">{`${index + 1}. `} </span>
                <span className={`text-omooma-pink-dark-primary ${isSafari ? 'rtl:font-bodyEn whitespace-pre-wrap' : 'rtl:font-bodyAr'}`}>{item.content}</span>
              </a>
            ))}
          </div>
        )}
        <div
          className="markdown mt-3 mb-8 "
          dangerouslySetInnerHTML={{ __html: postData?.content?.data?.content as string }}
        />

        {postData?.hubspotTag && (
          <div className="mb-8 max-w-xl mx-auto" dangerouslySetInnerHTML={{ __html: postData?.hubspotTag }}></div>
        )}
        {/* <script type="text/javascript" className="teads" defer src="//a.teads.tv/page/167256/tag"></script> */}

        <div>
          {postData?.sources?.data?.sources && (
            <div className="rounded-t-xl border w-full bg-white biography-content">
              <span className="mb-0 text-xl font-grey hover:omooma-pink-dark-primary " id="headingOne">
                <button
                  className={`${
                    activeElement === 'element1' &&
                    `text-primary [box-shadow:inset_0_-1px_0_rgba(229,231,235)] dark:!text-primary-400 dark:[box-shadow:inset_0_-1px_0_rgba(75,85,99)]`
                  } group relative flex w-full items-center rounded-t-[15px] border-0 bg-white px-5 py-4 text-left text-base text-neutral-600 transition [overflow-anchor:none] hover:z-[2] focus:z-[3] focus:outline-none`}
                  type="button"
                  onClick={() => handleClickAuthorSource('element1')}
                  aria-expanded="true"
                  aria-controls="collapseOne"
                >
                  {appContext?.currentLanguage === 'ar' ? (
                    <span className="sources-color rtl:font-bodyAr">المصادر والمراجع</span>
                  ) : (
                    'Sources and refrences'
                  )}
                  <span
                    className={`${
                      activeElement === 'element1'
                        ? `rotate-[-180deg] rtl:mr-2 ltr:mr-2`
                        : `rotate-0 fill-[#212529]  dark:fill-white`
                    } ml-auto h-5 w-5 shrink-0 fill-[#336dec] transition-transform duration-200 ease-in-out motion-reduce:transition-none dark:fill-blue-300`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="h-6 w-6"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </span>
                </button>
              </span>
              <TECollapse show={activeElement === 'element1'} className="!mt-0  !shadow-none">
                <div className="px-5 py-4 flex flex-row  w-full">
                  <div className="flex flex-col w-full font-light ">
                    <div
                      style={{ textDecoration: 'none', color: '#666666' }}
                      dangerouslySetInnerHTML={{
                        __html: addNofollowToLinks(postData?.sources?.data?.sources) || '',
                      }}
                    />
                  </div>
                </div>
              </TECollapse>
            </div>
          )}

          <div className="w-full rounded-b-xl bg-white flex flex-col  mb-3 p-6">
            <Link to={authorUrl(i18n.language, (postData?.expert?.slug ?? postData?.author?.slug) as string)}>
              <PostAuthor
                avatarUrl={(postData?.expert?.photo?.url ?? postData?.author?.photo?.url) as string}
                avatarAlt={
                  (postData?.expert?.photo?.alternativeText ?? postData?.author?.photo?.alternativeText) as string
                }
                name={(postData?.expert?.name ?? postData?.author?.name) as string}
                title={(postData?.expert?.title ?? postData?.author?.title) as string}
              />
            </Link>
            <span className="mb-2 mt-2 text-omooma-dark-light flex ltr:ml-14 rtl:mr-14">
              {
                <a
                  target="_blank"
                  href={
                    (postData?.expert?.socialLinks?.linkedinURL ?? postData?.author?.socialLinks?.linkedinURL) as string
                  }
                  className="icon-small"
                  aria-label="authors-linkedin-link"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    x="0px"
                    y="0px"
                    width="100"
                    height="100"
                    viewBox="0,0,256,256"
                  >
                    <g
                      fill="#ff7475"
                      fillRule="nonzero"
                      stroke="none"
                      strokeWidth="1"
                      strokeLinecap="butt"
                      strokeLinejoin="miter"
                      strokeMiterlimit="10"
                      strokeDasharray=""
                      strokeDashoffset="0"
                      fontFamily="none"
                      fontWeight="none"
                      fontSize="none"
                      textAnchor="none"
                    >
                      <g transform="scale(5.12,5.12)">
                        <path d="M9,4c-2.74952,0 -5,2.25048 -5,5v32c0,2.74952 2.25048,5 5,5h32c2.74952,0 5,-2.25048 5,-5v-32c0,-2.74952 -2.25048,-5 -5,-5zM9,6h32c1.66848,0 3,1.33152 3,3v32c0,1.66848 -1.33152,3 -3,3h-32c-1.66848,0 -3,-1.33152 -3,-3v-32c0,-1.66848 1.33152,-3 3,-3zM14,11.01172c-1.09522,0 -2.08078,0.32736 -2.81055,0.94141c-0.72977,0.61405 -1.17773,1.53139 -1.17773,2.51367c0,1.86718 1.61957,3.32281 3.67969,3.4668c0.0013,0.00065 0.0026,0.0013 0.00391,0.00195c0.09817,0.03346 0.20099,0.05126 0.30469,0.05273c2.27301,0 3.98828,-1.5922 3.98828,-3.52148c-0.00018,-0.01759 -0.00083,-0.03518 -0.00195,-0.05274c-0.10175,-1.90023 -1.79589,-3.40234 -3.98633,-3.40234zM14,12.98828c1.39223,0 1.94197,0.62176 2.00195,1.50391c-0.01215,0.85625 -0.54186,1.51953 -2.00195,1.51953c-1.38541,0 -2.01172,-0.70949 -2.01172,-1.54492c0,-0.41771 0.15242,-0.7325 0.47266,-1.00195c0.32023,-0.26945 0.83428,-0.47656 1.53906,-0.47656zM11,19c-0.55226,0.00006 -0.99994,0.44774 -1,1v19c0.00006,0.55226 0.44774,0.99994 1,1h6c0.55226,-0.00006 0.99994,-0.44774 1,-1v-5.86523v-13.13477c-0.00006,-0.55226 -0.44774,-0.99994 -1,-1zM20,19c-0.55226,0.00006 -0.99994,0.44774 -1,1v19c0.00006,0.55226 0.44774,0.99994 1,1h6c0.55226,-0.00006 0.99994,-0.44774 1,-1v-10c0,-0.82967 0.22639,-1.65497 0.625,-2.19531c0.39861,-0.54035 0.90147,-0.86463 1.85742,-0.84766c0.98574,0.01695 1.50758,0.35464 1.90234,0.88477c0.39477,0.53013 0.61523,1.32487 0.61523,2.1582v10c0.00006,0.55226 0.44774,0.99994 1,1h6c0.55226,-0.00006 0.99994,-0.44774 1,-1v-10.73828c0,-2.96154 -0.87721,-5.30739 -2.38086,-6.89453c-1.50365,-1.58714 -3.59497,-2.36719 -5.80664,-2.36719c-2.10202,0 -3.70165,0.70489 -4.8125,1.42383v-0.42383c-0.00006,-0.55226 -0.44774,-0.99994 -1,-1zM12,21h4v12.13477v4.86523h-4zM21,21h4v1.56055c0.00013,0.43 0.27511,0.81179 0.68291,0.94817c0.40781,0.13638 0.85714,-0.00319 1.11591,-0.34661c0,0 1.57037,-2.16211 5.01367,-2.16211c1.75333,0 3.25687,0.58258 4.35547,1.74219c1.0986,1.15961 1.83203,2.94607 1.83203,5.51953v9.73828h-4v-9c0,-1.16667 -0.27953,-2.37289 -1.00977,-3.35352c-0.73023,-0.98062 -1.9584,-1.66341 -3.47266,-1.68945c-1.52204,-0.02703 -2.77006,0.66996 -3.50195,1.66211c-0.73189,0.99215 -1.01562,2.21053 -1.01562,3.38086v9h-4z"></path>
                      </g>
                    </g>
                  </svg>
                </a>
              }
            </span>
            <div
              className="author-bio ltr:ml-14 rtl:mr-14 text-sm mt-1 text-omooma-dark-grey  font-extralight biography-content"
              dangerouslySetInnerHTML={{
                __html:
                  postData?.expert?.biography?.data?.biography ??
                  (postData?.author?.biography?.data?.biography as string),
              }}
            />
          </div>
        </div>
      </div>
      <div className="related-post-container bg-omomma-white">
        <div className="related-post-subcontainer lg:max-w-5xl mx-auto my-0 p-3 mb-0 ltr:font-body rtl:font-bodyAr flex flex-col text-omooma-dark">
          <h3 className=" lg:my-5 my-3 lg:text-2xl text-xl ltr:font-headline text-omooma-dark-grey rtl:font-bodyAr">
            {t('relatedPost')}
          </h3>
          <div className="">
            <PostRelatedArticles posts={postCategory?.posts} currentPostId={postData?.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const authorUrl = (language: string, slug: string | null): string => {
  if (language && language !== 'ar') return `/${language}/authors/${slug as string}`;
  else return `/authors/${slug as string}`;
};

export const query = graphql`
  query BlogPostPage($id: String!, $language: String!) {
    strapiBlogPost(id: { eq: $id }, locale: { eq: $language }) {
      id
      slug
      locale
      title
      publish_at
      publishedAt
      updatedAt
      hubspotTag
      video {
        url
      }
      image {
        url
        alternativeText
      }
      seo {
        metaDescription
        keywords
      }
      excerpt
      content {
        data {
          content
        }
      }
      sources {
        data {
          id
          sources
        }
      }
      localizations {
        data {
          id
          attributes {
            slug
            locale
          }
        }
      }
      categories {
        name
        slug
        weight
        posts {
          ...PostData
        }
      }
      video {
        url
      }
      youtubeVideoId
      dailymotionVideoId
      expert {
        slug
        name
        title
        socialLinks {
          linkedinURL
        }
        biography {
          data {
            biography
          }
        }
        photo {
          url
          alternativeText
        }
      }
      author {
        name
        slug
        title
        socialLinks {
          linkedinURL
        }
        biography {
          data {
            biography
          }
        }
        photo {
          url
          alternativeText
        }
      }
    }
    allStrapiLatestVideo {
      nodes {
        dailymotionVideoId
        title
        youtubeId
        locale
      }
    }
    locales: allLocale(filter: { ns: { in: ["common"] }, language: { eq: $language } }) {
      ...I18nPageTranslations
    }
  }
`;

export default BlogPostPage;
